import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, FileSpreadsheet, FileText, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { exportReceivings, fetchReceivings } from "@/lib/adminReceivingsApi";
import { fetchSuppliers } from "@/lib/adminSuppliersApi";
import type { Receiving, Supplier } from "@/lib/types";
import { hasPermission } from "@/lib/types";
import { useApp } from "@/store/useApp";
import { getApiErrorMessage } from "@/lib/api";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstDayOfMonthIso(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function fmtQty(value: number): string {
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

interface ReportRow {
  receivingId: string;
  date: string;
  supplierName: string;
  productName: string;
  unit: string;
  quantity: number;
}

function toRows(receivings: Receiving[]): ReportRow[] {
  const rows: ReportRow[] = [];
  for (const r of receivings) {
    for (const item of r.items) {
      rows.push({
        receivingId: r.id,
        date: r.receivedAt,
        supplierName: r.supplierName,
        productName: item.productName,
        unit: item.unit,
        quantity: item.quantity,
      });
    }
  }
  return rows;
}

export default function AdminReceivingsReport() {
  const canExport = hasPermission(useApp((s) => s.account), "RELATORIOS.RECEBIMENTOS.EXPORTAR");
  const [from, setFrom] = useState(firstDayOfMonthIso());
  const [to, setTo] = useState(todayIso());
  const [supplierId, setSupplierId] = useState<string>("");
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [receivings, setReceivings] = useState<Receiving[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "csv" | null>(null);

  useEffect(() => {
    fetchSuppliers()
      .then(setSuppliers)
      .catch(() => {});
  }, []);

  const sortedSuppliers = useMemo(
    () => [...suppliers].sort((a, b) => a.name.localeCompare(b.name)),
    [suppliers],
  );

  const rows = useMemo(() => (receivings ? toRows(receivings) : []), [receivings]);
  const totalQuantity = useMemo(() => rows.reduce((sum, r) => sum + r.quantity, 0), [rows]);

  const filters = useMemo(
    () => ({ from, to, supplierId: supplierId || undefined }),
    [from, to, supplierId],
  );

  const handleGenerate = useCallback(async () => {
    if (!from || !to) {
      toast.error("Informe o período");
      return;
    }
    setLoading(true);
    try {
      const result = await fetchReceivings(filters);
      setReceivings(result);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Erro ao gerar relatório"));
    } finally {
      setLoading(false);
    }
  }, [from, to, filters]);

  const handleExport = useCallback(
    async (format: "pdf" | "csv") => {
      if (!from || !to) {
        toast.error("Informe o período");
        return;
      }
      setExporting(format);
      try {
        await exportReceivings(format, filters);
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Erro ao exportar"));
      } finally {
        setExporting(null);
      }
    },
    [from, to, filters],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Truck className="h-5 w-5" /> Relatório de Recebimentos
            </h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Entradas de mercadoria por dia, período e fornecedor.
            </p>
          </div>
          {receivings && canExport && (
            <div className="flex gap-2">
              <Button variant="secondary" className="gap-2" onClick={() => void handleExport("pdf")} disabled={exporting !== null}>
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                PDF
              </Button>
              <Button variant="secondary" className="gap-2" onClick={() => void handleExport("csv")} disabled={exporting !== null}>
                {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                CSV
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_2fr_auto] items-end">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">De</Label>
            <DatePicker value={from} onChange={setFrom} />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Até</Label>
            <DatePicker value={to} onChange={setTo} />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Fornecedor</Label>
            <Select value={supplierId || "__all__"} onValueChange={(v) => setSupplierId(v === "__all__" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Todos os fornecedores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os fornecedores</SelectItem>
                {sortedSuppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => void handleGenerate()} disabled={loading} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
            Gerar
          </Button>
        </div>
      </div>

      {receivings && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <StatCard label="Recebimentos" value={String(receivings.length)} />
          <StatCard label="Itens" value={String(rows.length)} />
          <StatCard label="Quantidade total" value={fmtQty(totalQuantity)} />
        </div>
      )}

      {receivings && rows.length === 0 && (
        <div className="rounded-xl border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          Nenhum recebimento encontrado nesse filtro.
        </div>
      )}

      {receivings && rows.length > 0 && (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Data</th>
                  <th className="px-4 py-2 text-left">Fornecedor</th>
                  <th className="px-4 py-2 text-left">Produto</th>
                  <th className="px-4 py-2 text-center">Unidade</th>
                  <th className="px-4 py-2 text-right">Quantidade</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={`${r.receivingId}-${r.productName}-${idx}`} className="border-t">
                    <td className="px-4 py-2 whitespace-nowrap">{fmtDateTime(r.date)}</td>
                    <td className="px-4 py-2">{r.supplierName}</td>
                    <td className="px-4 py-2">{r.productName}</td>
                    <td className="px-4 py-2 text-center text-muted-foreground">{r.unit}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtQty(r.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!receivings && !loading && (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/30 py-16 text-center text-sm text-muted-foreground">
          <Download className="h-6 w-6" />
          Informe o período e clique em <strong>Gerar</strong> para ver o relatório.
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
